import _ from 'lodash'
import {basicContext} from "../../utils/contextUtils"
import {decrypt} from "../../utils/crypto"
import {changeFieldProperty, setDataList, setFieldListOptions, setFieldVisibility} from "../../../apps/KpModule/actions"
import {fieldPathJoinGetter} from "../../utils/kp3Utils"
import {openingHoursField, validateClosedEstablishment, validateOpeningHours} from "./shelter"
import {
    answerOptions,
    establishmentStatusObjects,
    memberObjects,
    newOrganizationTypes, newStateObjects,
    activityNames,
    paymentMethodOptions
} from "./staticEntities";
import Errors from "../../utils/Errors";
import async from "async";
import {getFieldsByPath} from "./liberality/utils";
import {clearOtherActivity, otherActivityFormField, refreshOtherActivityField, relabelOtherActivityOption} from "./utils/otherActivity";

const ESTABLISHMENT_TYPES_LIST = "m-C-organization.Shelter_types"
import {generateFetchFieldListAction} from "../../../apps/KpModule/actions/api";

const defaultRup = answerOptions.find(obj => obj.id === 'idk')
const defaultMember = memberObjects.find(obj => obj.id === '3')

const customFieldPath = ["id", "_createdInTheForm", 'siretNameStatus']
const customUserFieldPath = ["id", "completeInfo", "_createdInTheForm"]

export const entities = [
    {
        name: "DtObjectsColumn",
        type: "static",
        fields: [
            {path: "data", type: 'integer'},
            {path: "type"}
        ]
    },
    {
        name: "DateDtObjectsColumn",
        type: "static",
        fields: [
            {path: "data", type: 'date'},
            {path: "type"}
        ]
    },
    {
        name: "BooleanDtObjectsColumn",
        type: "static",
        fields: [
            {path: "data", type: 'boolean'},
            {path: "type"}
        ]
    },
    {
        name: 'OrganizationDetail',
        fields: [
            'Exercise',
            {path: 'administratorsNumber', type: 'integer'},
            {path: 'employeesNumber', type: 'integer'},
            {path: 'volunteersNumber', type: 'integer'},
            {path: 'investigatorsNumber', type: 'integer'},
            {path: 'hostFamiliesNumber', type: 'integer'},
            {path: 'ACACEDNumber', type: 'integer'},
        ]
    },
    {
        name: "ManualOrganizationDetail",
        type: "mongoInternal",
        fields: [
            'Exercise',
            {path: 'administratorsNumber', type: "DtObjectsColumn"},
            {path: 'employeesNumber', type: "DtObjectsColumn"},
            {path: 'volunteersNumber', type: "DtObjectsColumn"},
            {path: 'investigatorsNumber', type: "DtObjectsColumn"},
            {path: 'hostFamiliesNumber', type: "DtObjectsColumn"},
            {path: 'ACACEDNumber', type: "DtObjectsColumn"},
        ]
    },
    {
        name: 'OrganizationMembership',
        facets: [
            {name: 'files', linkType: 'OTO', path: 'membershipCard'},
            {name: 'files', linkType: 'OTO', path: 'accountStatement'},
        ],
        fields: [
            'Exercise',
            {path: 'status', type: 'CotisationStatus', nullable: true},
            {path: 'upToDate', type: 'boolean', nullable: true},
            {path: 'amount', type: 'decimal', nullable: true},
            {path: 'feeAmount', type: 'decimal', nullable: true},
            {path: 'donationAmount', type: 'decimal', nullable: true},
            {path: 'paymentDate', type: 'date', nullable: true},
            {path: 'paymentMethod', type: 'PaymentMethod', nullable: true},
            {path: 'sendingCounter', type: 'integer', default: 0, nullable: true},
            {path: 'lastModificationUser', type: 'User', nullable: true },
            {path: 'lastModificationDate', type: 'date', nullable: true },
        ]
    },
    {
        name: "OrganizationImage",
        type: "mongoInternal",
        fields: [
            {path: 'name', notEmpty: true},
            'User',
            {path: 'date', notEmpty: true}
        ]
    },
    {
        name: 'Organization',
        facets: [{name: 'codeName', codeTKey: 'siren_spec', nameTKey: 'denomination_spec', nameOptional: false, uniqueName: false}, 'conversations', 'files'],
        fields: [
            {path: 'organizationType', type: 'NewOrganizationType'},
            {type: 'Member', nullable: true},
            'address',
            'additionalAddress',
            'zipCode',
            'city',
            //{path: 'zone', type: 'Zone'},
            'country',
            //'siren',
            //'siret',
            'rna',
            'accountNumber',
            {path: 'iban', encrypted: true},
            {path: 'bic', encrypted: true},
            {path: 'eligibleForDirectAidPayment', type: 'boolean'},
            {path: 'rup', type: 'AnswerOption'},
            {path: 'endowmentFund', type: 'boolean'},
            'endowmentFundDetails',
            {path: 'activities', type: 'NewOrganizationActivity', link: 'MTM', nullable: true},
            'otherActivity',
            {
                path: 'activitiesLabel',
                fieldPath: ['activities.id', 'activities.name', 'otherActivity'],
                $f: (organization, context, callback) => {
                    return callback(null, activityNames(organization.activities, organization.otherActivity).join(', '))
                }
            },
            {path: 'logo', type: 'OrganizationImage', link: "OTO"},
            {path: 'images', type: 'OrganizationImage', link: 'OTM'},

            { path: 'createdAt', type: 'date', nullable: true },
            { path: 'createdBy', type: 'User', nullable: true },

            { path: 'lastUser', type: 'User', nullable: true },
            { path: 'lastModificationDate', type: 'date', nullable: true },
            fieldPathJoinGetter({path: "sirenTradingName", fieldPath: ["code", "tradingName"]}),
            fieldPathJoinGetter({
                path: "zipCodeAndName",
                fieldPath: ["zipCode", "tradingName"],
                joinString: " - "
            }),
            {
                path: 'hasShelters',
                fieldPath: ['establishments.id'],
                $f: async (organization, context, callback) => {
                    const shelter = organization.establishments.length
                    return callback(null, !!shelter)
                }
            },
            {
                path: 'advanceRecoveryImputations',
                fieldPath: ['id'],
                $f: (organization, context, callback) => {
                    const beneficiaryId = new global.ObjectID(organization.id)
                    Promise.all([
                        global.app.C.LiberalityReversionDeparture.find({
                            ...basicContext(context),
                            query: {beneficiary: beneficiaryId, departureForm: 'advanceRecovery'},
                            fieldPath: [
                                'id',
                                'date',
                                'advanceRecoveryAmount',
                                'comment',
                                'asset.identifier',
                                'asset.applicationNumber',
                                'asset.typeOfFile',
                                'asset.liberalityRecord.presentationDate',
                                'asset.liberalityRecord.decisionDate',
                                'asset.liberalityRecord.benefactor',
                                'asset.liberalityRecord.status.id', 'asset.liberalityRecord.status.name', 'asset.liberalityRecord.status.bStyle',
                                'asset.liberalityRecord.administrator.fullName',
                            ]
                        }),
                        global.app.C.AssetPartition.find({
                            ...basicContext(context),
                            query: {beneficiary: beneficiaryId},
                            fieldPath: ['id', 'assetInfos.id', 'qualification.id', 'qualification.name']
                        })
                    ]).then(([departures, partitions]) => {
                        const qualificationByAssetId = _.fromPairs(
                            partitions.map(partition => [partition.assetInfos?.id, _.pick(partition.qualification, ['id', 'name'])])
                        )
                        callback(null, departures.map(departure => ({
                            id: departure.id,
                            date: departure.date,
                            advanceRecoveryAmount: departure.advanceRecoveryAmount,
                            comment: departure.comment,
                            qualification: qualificationByAssetId[departure.asset?.id],
                            asset: {
                                ..._.pick(departure.asset, ['id', 'identifier', 'applicationNumber', 'typeOfFile']),
                                liberalityRecord: {
                                    presentationDate: departure.asset?.liberalityRecord?.presentationDate,
                                    decisionDate: departure.asset?.liberalityRecord?.decisionDate,
                                    benefactor: departure.asset?.liberalityRecord?.benefactor,
                                    status: _.pick(departure.asset?.liberalityRecord?.status, ['id', 'name', 'bStyle']),
                                    administrator: _.pick(departure.asset?.liberalityRecord?.administrator, ['id', 'fullName']),
                                },
                            },
                        })))
                    }).catch(callback)
                }
            },
            {
                path: 'liberalityBeneficiaryFiles',
                fieldPath: ['id'],
                $f: (organization, context, callback) => {
                    global.app.C.AssetPartition.find({
                        ...basicContext(context),
                        query: {beneficiary: new global.ObjectID(organization.id)},
                        fieldPath: [
                            'id',
                            'assetInfos.liberalityRecord.applicationNumber',
                            'assetInfos.liberalityRecord.status.id', 'assetInfos.liberalityRecord.status.name', 'assetInfos.liberalityRecord.status.bStyle',
                            'assetInfos.liberalityRecord.decisionDate',
                            'assetInfos.liberalityRecord.administrator.fullName',
                            'assetInfos.liberalityRecord.typeOfFile.name',
                            'assetInfos.liberalityRecord.presentationDate',
                            'assetInfos.liberalityRecord.benefactor',
                            'assetInfos.identifier',
                            'qualification.id', 'qualification.name',
                            'estimatedBeneficiaryNetValue',
                            'CNDAShareInEstimatedBeneficiaryNetValue',
                            'assetInfos.status.id', 'assetInfos.status.name',
                        ]
                    }).then(list => {
                        const files = list.map(partition => ({
                            id: partition.id,
                            estimatedBeneficiaryNetValue: partition.estimatedBeneficiaryNetValue,
                            CNDAShareInEstimatedBeneficiaryNetValue: partition.CNDAShareInEstimatedBeneficiaryNetValue,
                            qualification: _.pick(partition.qualification, ['id', 'name']),
                            assetInfos: {
                                id: partition.assetInfos?.id,
                                identifier: partition.assetInfos?.identifier,
                                status: _.pick(partition.assetInfos?.status, ['id', 'name']),
                                liberalityRecord: {
                                    id: partition.assetInfos?.liberalityRecord?.id,
                                    applicationNumber: partition.assetInfos?.liberalityRecord?.applicationNumber,
                                    decisionDate: partition.assetInfos?.liberalityRecord?.decisionDate,
                                    presentationDate: partition.assetInfos?.liberalityRecord?.presentationDate,
                                    benefactor: partition.assetInfos?.liberalityRecord?.benefactor,
                                    status: _.pick(partition.assetInfos?.liberalityRecord?.status, ['id', 'name', 'bStyle']),
                                    administrator: _.pick(partition.assetInfos?.liberalityRecord?.administrator, ['id', 'fullName']),
                                    typeOfFile: _.pick(partition.assetInfos?.liberalityRecord?.typeOfFile, ['id', 'name']),
                                },
                            },
                        }))
                        callback(null, files)
                    }).catch(callback)
                }
            },
            {
                path: 'usersNumber',
                fieldPath: ['users.id'],
                $f: (organization, context, callback) => {
                    return callback(null, (organization.users || []).length)
                }
            },
            {
                path: 'users',
                type: 'CUser',
                link: {
                    type: 'OTM',
                    deleteType: 'block'
                }
            },
            {
                path: 'colleagueFiles',
                type: 'ColleagueFile',
                link: "OTM",
                fieldPath: ['files.id', 'files.filename', 'files.date', 'files.user.id', 'files.user.name', 'users.kpUser.id'],
                $f: function(organization, context, callback) {
                    const colleagueKpUserIds = new Set(
                        (organization.users || []).map(u => u.kpUser?.id).filter(Boolean)
                    )
                    return callback(null, (organization.files || []).filter(
                        file => file.user && colleagueKpUserIds.has(file.user.id)
                    ))
                },
                $s: function(newColleagueFiles, organization, context, callback) {
                    const colleagueKpUserIds = new Set(
                        (organization.users || []).map(u => u.kpUser?.id).filter(Boolean)
                    )
                    const nonColleagueFiles = (organization.files || []).filter(
                        f => !f.user || !colleagueKpUserIds.has(f.user.id)
                    )
                    organization.files = [...nonColleagueFiles, ...(newColleagueFiles || [])]
                    callback(null, newColleagueFiles)
                }
            },
            {
                path: 'ongoingAdministratorsNumber',
                nullable: true,
                fieldPath: ['organizationDetails.administratorsNumber', 'organizationDetails.exercise.exerciseStatus.id'],
                $f: (organization, context, callback) => {
                    const organizationDetailsForOngoingExercise = organization.organizationDetails?.find(object => _.get(object, 'exercise.exerciseStatus.id') === 'ongoing')
                    return callback(null, organizationDetailsForOngoingExercise?.administratorsNumber)
                }
            },
            {
                path: 'ongoingEmployeesNumber',
                nullable: true,
                fieldPath: ['organizationDetails.employeesNumber', 'organizationDetails.exercise.exerciseStatus.id'],
                $f: (organization, context, callback) => {
                    const organizationDetailsForOngoingExercise = organization.organizationDetails?.find(object => _.get(object, 'exercise.exerciseStatus.id') === 'ongoing')
                    return callback(null, organizationDetailsForOngoingExercise?.employeesNumber)
                }
            },
            {
                path: 'ongoingVolunteersNumber',
                nullable: true,
                fieldPath: ['organizationDetails.volunteersNumber', 'organizationDetails.exercise.exerciseStatus.id'],
                $f: (organization, context, callback) => {
                    const organizationDetailsForOngoingExercise = organization.organizationDetails?.find(object => _.get(object, 'exercise.exerciseStatus.id') === 'ongoing')
                    return callback(null, organizationDetailsForOngoingExercise?.volunteersNumber)
                }
            },
            {
                path: 'ongoingInvestigatorsNumber',
                nullable: true,
                fieldPath: ['organizationDetails.investigatorsNumber', 'organizationDetails.exercise.exerciseStatus.id'],
                $f: (organization, context, callback) => {
                    const organizationDetailsForOngoingExercise = organization.organizationDetails?.find(object => _.get(object, 'exercise.exerciseStatus.id') === 'ongoing')
                    return callback(null, organizationDetailsForOngoingExercise?.investigatorsNumber)
                }
            },
            {
                path: 'ongoingHostFamiliesNumber',
                nullable: true,
                fieldPath: ['organizationDetails.hostFamiliesNumber', 'organizationDetails.exercise.exerciseStatus.id'],
                $f: (organization, context, callback) => {
                    const organizationDetailsForOngoingExercise = organization.organizationDetails?.find(object => _.get(object, 'exercise.exerciseStatus.id') === 'ongoing')
                    return callback(null, organizationDetailsForOngoingExercise?.hostFamiliesNumber)
                }
            },
            {
                path: 'ongoingACACEDNumber',
                nullable: true,
                fieldPath: ['organizationDetails.ACACEDNumber', 'organizationDetails.exercise.exerciseStatus.id'],
                $f: (organization, context, callback) => {
                    const organizationDetailsForOngoingExercise = organization.organizationDetails?.find(object => _.get(object, 'exercise.exerciseStatus.id') === 'ongoing')
                    return callback(null, organizationDetailsForOngoingExercise?.ACACEDNumber)
                }
            },
            {
                path: 'manualOrganizationDetails',
                type: 'ManualOrganizationDetail',
                fieldPath: ['organizationDetails.id', 'organizationDetails.exercise.id'],
                //lazy: true,
                $f: (organization,context,callback) => {
                    global.app.C.Exercise.find({
                        ...basicContext(context),
                        fieldPath: ['id', 'exerciseStatus.id'],
                        query: {}
                    }, (e, exercises) => {
                        const objects = exercises.map(exercise => {
                            const type = exercise.exerciseStatus.id === 'finalClosure' ? 'text' : 'editText'
                            const exerciseDetails = organization.organizationDetails?.find(details => details.exercise?.id === exercise.id)
                            return exerciseDetails
                                ? {
                                    ..._.pick(exerciseDetails, 'id', 'exercise'),
                                    administratorsNumber: {data: exerciseDetails.administratorsNumber || 0, type},
                                    employeesNumber: {data: exerciseDetails.employeesNumber || 0, type},
                                    volunteersNumber: {data: exerciseDetails.volunteersNumber || 0, type},
                                    investigatorsNumber: {data: exerciseDetails.investigatorsNumber || 0, type},
                                    hostFamiliesNumber: {data: exerciseDetails.hostFamiliesNumber || 0, type},
                                    ACACEDNumber: {data: exerciseDetails.ACACEDNumber || 0, type},
                                }
                                : {
                                    id: new global.ObjectID(),
                                    exercise: _.pick(exercise, ['id', 'code']),
                                    administratorsNumber: {data: 0, type},
                                    employeesNumber: {data: 0, type},
                                    volunteersNumber: {data: 0, type},
                                    investigatorsNumber: {data: 0, type},
                                    hostFamiliesNumber: {data: 0, type},
                                    ACACEDNumber: {data: 0, type},
                                }
                        })
                        return callback(null, objects)
                    })
                },
                $s: ( manualOrganizationDetails, organization, context, callback) => {
                    organization.organizationDetails = manualOrganizationDetails.map(object => {
                        return {
                            ..._.pick(object, 'id', 'exercise'),
                            administratorsNumber: object.administratorsNumber.data,
                            employeesNumber: object.employeesNumber.data,
                            volunteersNumber: object.volunteersNumber.data,
                            investigatorsNumber: object.investigatorsNumber.data,
                            hostFamiliesNumber: object.hostFamiliesNumber.data,
                            ACACEDNumber: object.ACACEDNumber.data,
                        }
                    })
                    organization.manualOrganizationDetails = []
                    callback()
                }
            },
            {
                path: 'organizationDetails',
                type: 'OrganizationDetail',
                link: {
                    type: 'OTM',
                    onParent: true,
                    deleteType: 'cascade'
                }
            },
            {
                path: 'organizationMemberships',
                type: 'OrganizationMembership',
                link: {
                    type: 'OTM',
                    onParent: true,
                    deleteType: 'cascade'
                }
            },

            //added
            'appellation',
            'tradingName',
            {path: 'status', type: 'OrganizationStatus', nullable: true},
            {
                path: 'establishments',
                type: 'Shelter',
                link: {
                    type: 'OTM',
                    deleteType: 'block'
                }
            },
            {
                path: 'headquarterSiretGetter',
                fieldPath: ['establishments.id', 'establishments.code', 'establishments.status'],
                $f: async (organization, context, callback) => {
                    const headquarter = organization.establishments.find(shelter => shelter.status?.id === 'headquarters')
                    return callback(null, headquarter?.code)
                }
            },
            {path: 'animalTypes', type: 'AnimalType', link: 'MTM', nullable: true},
            {
                path: 'nbEstablishments',
                type: 'integer',
                fieldPath: ['establishments.id', 'establishments.status'],
                $f: async (organization, context, callback) => {
                    const establishments = organization.establishments.filter(shelter => ['headquarters', 'secondary'].includes(shelter.status?.id))
                    return callback(null, establishments.length)
                }
            },
            {
                path: 'nbContacts',
                type: 'integer',
                fieldPath: ['users.id', 'users.state'],
                $f: async (organization, context, callback) => {
                    const activeContacts = organization.users.filter(contact => contact.active)
                    return callback(null, activeContacts.length)
                }
            },
            {
                path: 'nbRefuges',
                type: 'integer',
                fieldPath: ['refuges.id', 'refuges.state'],
                $f: async (organization, context, callback) => {
                    const activeRefuges = organization.refuges.filter(obj => obj.state?.id === 'active')
                    return callback(null, activeRefuges.length)
                }
            },
            {
                path: 'refuges',
                type: 'Refuge',
                link: {
                    type: 'OTM',
                    deleteType: 'block'
                }
            },
            {path: 'statusDate', type: 'date', nullable: true},
        ],
        filters: [
            {
                name: "isEligibleForDirectAidPayment",
                isDefault: false,
                query: () => ({eligibleForDirectAidPayment : true })
            },
            {
                name: "isSupplier",
                isDefault: false,
                async: true,
                query: (context, callback) => {
                    callback(null, {organizationType: 'supplier'})
                }
            },
            {
                name: 'isAssociation',
                isDefault: false,
                async: true,
                query: (context, callback) => {
                    callback(null, {organizationType: {$in: ['association', 'internal']}})
                }
            },
            {
                name: "isMemberAssociation",
                isDefault: false,
                async: true,
                query: (context, callback) => {
                    callback(null, {organizationType: 'association', status: 'member'})
                }
            },
            {
                name: "byUser",
                isDefault: false,
                async: true,
                query: function(context, callback) {
                    global.app.C.CUser.get({kpUser: new global.ObjectID(context.user.id)}, {
                        ...basicContext(context),
                        fieldPath: ['id', 'organization.id'],
                    }, (error, cUser) => {
                        if(error) return callback(error)
                        callback(null, {
                            _id: cUser
                                ? new global.ObjectID(cUser.organization.id)
                                : null
                        })
                    })
                }
            },
            {
                // Internal organization users see every organization, the others only their own.
                name: "visibleByUser",
                isDefault: false,
                async: true,
                query: function(context, callback) {
                    global.app.C.CUser.get({kpUser: new global.ObjectID(context.user.id)}, {
                        ...basicContext(context),
                        fieldPath: ['id', 'organization.id', 'organization.organizationType.id'],
                        ignoreNotFound: true
                    }, (error, cUser) => {
                        if(error) return callback(error)
                        if(!cUser?.organization?.id) return callback(null, {_id: null})
                        if(cUser.organization.organizationType?.id === 'internal') return callback(null, {})
                        callback(null, {_id: new global.ObjectID(cUser.organization.id)})
                    })
                }
            },
            {
                name: 'inAssetPartition',
                isDefault: false,
                async: true,
                query: async function(context, callback) {
                    const parts = await global.app.C.AssetPartition.find({
                        ...basicContext(context),
                        query: {},
                        fieldPath: ['beneficiary.id']
                    })
                    const ids = [...new Set(parts.map(p => p.beneficiary?.id).filter(Boolean))]
                    return callback(null, {_id: {$in: ids.map(id => new global.ObjectID(id))}})
                }
            },
            {
                name: 'inLiberalityRecordDistribution',
                isDefault: false,
                async: true,
                query: async function(context, callback) {
                    const liberalityRecordId = _.get(context, "data.liberalityRecordId")
                    if (!liberalityRecordId) return callback(null, {_id: null})

                    const liberalityRecord = await global.app.C.LiberalityRecord.get(liberalityRecordId, {
                        ...basicContext(context),
                        fieldPath: ['assetsInfos.distribution.beneficiary.id']
                    })

                    const ids = [...new Set(
                        _.flatMap(liberalityRecord?.assetsInfos || [], assetInfo => assetInfo.distribution || [])
                            .map(partition => partition.beneficiary?.id)
                            .filter(Boolean)
                    )]
                    return callback(null, {_id: {$in: ids.map(id => new global.ObjectID(id))}})
                }
            },
            {
                name: 'byType',
                isDefault: false,
                path: "organizationType",
                tKey: "type",
                placeholder: "Sélectionner un type",
                object: "NewOrganizationType",
                display: "name",
                client: true,
                default: {id: 'association'},
                query: function (context) {
                    const organizationType = _.get(context, "data.organizationType")

                    return organizationType
                        ? {organizationType: organizationType.id}
                        : {}
                }
            },
            {
                name: 'byStatus',
                isDefault: false,
                path: "status",
                tKey: "status",
                placeholder: "Sélectionner un statut",
                object: "OrganizationStatus",
                display: "name",
                type: 'tags',
                default: [{id: 'member'}],
                client: true,
                query: function (context) {
                    const organizationStatus = _.get(context, "data.status")

                    return organizationStatus ? {status: {$in: organizationStatus.map(o => o.id)}} : {}
                }
            }
        ],
        checkAccountNumberUnicity: async function(newObject, oldObject, context, callback) {
            if(!newObject.accountNumber) return callback()

            const newOrganizations = await global.app.C.Organization.find({
                ...basicContext(context),
                fieldPath: [],
                query: {
                    _id: {$ne: new global.ObjectID(newObject.id)},
                    accountNumber: newObject.accountNumber
                }
            })

            if(!!newOrganizations.length) return callback(new Errors.ValidationError('Le champ [N° compte comptable] doit être unique'))
            return callback()
        },
        checkIBANUnicity: async function(newObject, oldObject, context, callback) {
            if(!newObject.iban) return callback()

            const newOrganizations = await global.app.C.Organization.find({
                ...basicContext(context),
                fieldPath: [],
                query: {
                    _id: {$ne: new global.ObjectID(newObject.id)},
                    iban: newObject.iban
                }
            })

            if(!!newOrganizations.length) return callback(new Errors.ValidationError('Le champ [IBAN] doit être unique'))
            return callback()
        },
        checkRemovedActivitiesNotUsedByEstablishments: async function(newObject, oldObject, context, callback) {
            const removedActivities = (oldObject?.activities || []).filter(
                activity => !(newObject.activities || []).some(newActivity => newActivity.id === activity.id)
            )
            if(!removedActivities.length) return callback()

            const shelters = await global.app.C.Shelter.find({
                ...basicContext(context),
                fieldPath: [],
                query: {
                    organization: new global.ObjectID(newObject.id),
                    types: {$in: removedActivities.map(activity => activity.id)}
                }
            })

            if(!!shelters.length) return callback(new Errors.ValidationError("Vous ne pouvez pas supprimer une activité associée à au moins un établissement de l'organisation"))
            return callback()
        },
        // Same check as Shelter.validateSave, for opening hours edited from the establishments accordion.
        checkEstablishmentsOpeningHours: function(newObject, callback) {
            for (const establishment of newObject.establishments || []) {
                const error = validateOpeningHours(establishment.openingHours)
                if (error) return callback(new Errors.ValidationError(`${establishment.name} : ${error}`))
            }
            return callback()
        },
        // Same check as Shelter.validateSave, for a status edited from the establishments accordion.
        checkClosedEstablishments: async function(newObject, context, callback) {
            for (const establishment of newObject.establishments || []) {
                const error = await validateClosedEstablishment(establishment, context)
                if (error) return callback(new Errors.ValidationError(`${establishment.name} : ${error}`))
            }
            return callback()
        },
        validateSave: function(newObject, oldObject, context, callback) {
            async.series([
                callback => this.checkAccountNumberUnicity(newObject, oldObject, context, callback),
                callback => this.checkIBANUnicity(newObject, oldObject, context, callback),
                callback => this.checkRemovedActivitiesNotUsedByEstablishments(newObject, oldObject, context, callback),
                callback => this.checkEstablishmentsOpeningHours(newObject, callback),
                callback => this.checkClosedEstablishments(newObject, context, callback)
            ], callback)
        },
        beforeSave: (object, oldObject, context, callback) => {
            if (context.clientContext.moduleId !== 'm-C-membershipFee') object.logo = _.sortBy(object.images, 'date' ).reverse()[0]

            if (context.restAction && context.restAction.crudType === "C") {
                object.createdAt = new Date()
                object.createdBy = new global.ObjectID(context.user.id)
            }

            clearOtherActivity(object, 'activities')

            object.lastUser = new global.ObjectID(context.user.id)
            object.lastModificationDate = new Date()
            callback(null, object, oldObject)
        }
    }
]

export const module_ = {
    object: 'Organization',
    tKey: 'mTitle_organization',
    defaultSortBy: 'code',
    objectIdentifier: 'code',
    defaultSortDirection: 'ASC',
    category: {
        path: 'menuTitle_phoneBook',
        icon: 'notebook'
    },
    formSections: [
        {
            id: 'address',
            tKey: "Adresse de gestion",
            defaultCollapsed: true,
            //color: "rgb(252, 185, 0)"
        },
        {
            id: 'administratif',
            tKey: "Administratif",
            defaultCollapsed: true
        },
        {
            id: 'logo',
            tKey: "Logo",
            defaultCollapsed: true
        },
        {
            id: 'documents',
            tKey: "Documents",
            defaultCollapsed: true
        },
        {
            id: 'chat',
            tKey: "Historique d'échanges",
            defaultCollapsed: true
        }
    ],
    viewMap: {
        dt: [
            {path: 'organizationType', tKey: 'type_spec'},
            {path: 'tradingName'},
            {path: 'code', tKey: 'siren_spec'},
            {path: 'name', tKey: 'denomination_spec', initiallyNotVisible: true},
            {path: 'rna', tKey: 'rna_spec', initiallyNotVisible: true},
            {path: 'appellation', tKey: 'appellation', initiallyNotVisible: true},
            {path: 'rup', initiallyNotVisible: true},
            {path: 'endowmentFund', initiallyNotVisible: true},
            {path: 'status', tKey: 'status', initiallyNotVisible: true},
            {path: 'member', tKey: 'organizationSize', initiallyNotVisible: true},
            {path: 'activitiesLabel', tKey: 'activities_spec', initiallyNotVisible: true},
            {path: 'animalTypes', tKey: 'species', initiallyNotVisible: true},
            {path: 'nbEstablishments', tKey: 'establishmentsNumber'},
            {path: 'nbContacts', tKey: 'contactsNumber_spec'},
            {path: 'nbRefuges', initiallyNotVisible: true},
            {path: 'address', initiallyNotVisible: true},
            {path: 'additionalAddress', initiallyNotVisible: true},
            {path: 'zipCode'},
            {path: 'city', initiallyNotVisible: true},
            {path: 'lastUser'},
            {path: 'lastModificationDate'},
        ],
        form: {
            fields: [
                {
                    path: 'organizationType',
                    widthClassName: "col-xs-12 col-sm-6",
                    tKey: 'type_spec',
                    required: true,
                    placeholder: 'Sélectionner un type',
                    default: newOrganizationTypes.find(type => type.id === 'association'),
                    editable: false,
                    clearable: false,
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const state = store.getState()
                            const objectMode = state.ui.objectMode
                            const neededFormFields = getFieldsByPath(module.viewMap.form.fields, [
                                'eligibleForDirectAidPayment', 'rna', 'organizationMemberships',
                                'manualOrganizationDetails', 'rup', 'endowmentFund', 'member', 'activities', 'animalTypes',
                                'status', 'establishments', 'users', 'country', 'appellation', 'refuges']);
                            const neededEstablishmentFields = getFieldsByPath(neededFormFields?.establishments.viewMap.form.fields, ['types', 'animalTypes']);
                            const neededContactsFields = getFieldsByPath(neededFormFields?.users.viewMap.form.fields, ['organizationReferent', 'liberalityReferent']);

                            const isAssociation = newValue?.id === 'association'
                            const isSupplier = newValue?.id === 'supplier'
                            const isInternal = newValue?.id === 'internal'
                            const changedValue = !!oldValue?.id && !!newValue?.id && oldValue.id !== newValue.id

                            store.dispatch(setFieldVisibility(neededFormFields?.eligibleForDirectAidPayment.id, isSupplier))
                            store.dispatch(setFieldVisibility(neededFormFields?.rna.id, isAssociation || isInternal))
                            store.dispatch(setFieldVisibility(neededFormFields?.appellation.id, isAssociation || isInternal))
                            store.dispatch(setFieldVisibility(neededFormFields?.organizationMemberships.id, isAssociation && objectMode !== 'newObject'))
                            store.dispatch(setFieldVisibility(neededFormFields?.manualOrganizationDetails.id, isAssociation && objectMode !== 'newObject'))
                            store.dispatch(setFieldVisibility(neededFormFields?.rup.id, isAssociation || isInternal))
                            store.dispatch(setFieldVisibility(neededFormFields?.endowmentFund.id, isAssociation || isInternal))
                            store.dispatch(setFieldVisibility(neededFormFields?.member.id, isAssociation))
                            store.dispatch(setFieldVisibility(neededFormFields?.activities.id, isAssociation))
                            refreshOtherActivityField({store, module}, 'activities', isAssociation)
                            store.dispatch(setFieldVisibility(neededFormFields?.animalTypes.id, isAssociation))
                            store.dispatch(setFieldVisibility(neededFormFields?.status.id, isAssociation))
                            store.dispatch(setFieldVisibility(neededFormFields?.refuges.id, isAssociation))

                            store.dispatch(setFieldVisibility(neededEstablishmentFields?.types.id, isAssociation))
                            store.dispatch(setFieldVisibility(neededEstablishmentFields?.animalTypes.id, isAssociation))
                            store.dispatch(setFieldVisibility(neededContactsFields?.organizationReferent.id, isAssociation))
                            store.dispatch(setFieldVisibility(neededContactsFields?.liberalityReferent.id, isAssociation))

                            if (objectMode === 'newObject' && changedValue){
                                neededFormFields?.rna.setValue('')
                                neededFormFields?.appellation.setValue('')
                                neededFormFields?.rup.setValue(defaultRup)
                                neededFormFields?.endowmentFund.setValue(false)
                                neededFormFields?.member.setValue(defaultMember)
                                neededFormFields?.activities.setValue([])
                                neededFormFields?.animalTypes.setValue([])
                                neededFormFields?.status.setValue(null)
                                neededFormFields?.eligibleForDirectAidPayment.setValue(false)
                                neededFormFields?.establishments.setValue([])
                                neededFormFields?.users.setValue([])
                                neededFormFields?.refuges.setValue([])
                            }
                        }
                    }
                },
                {
                    path: 'tradingName',
                    placeholder: "Renseigner le Nom d'usage",
                    widthClassName: "col-xs-12 col-sm-6",
                },
                {
                    path: 'code',
                    tKey: 'siren_spec',
                    widthClassName: "col-xs-12 col-sm-6",
                    required: true,
                    exactLength: 9,
                    numerical: true,
                    placeholder: 'Renseigner le N° SIREN',
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const neededFormFields = getFieldsByPath(module.viewMap.form.fields, ['establishments']);
                            const neededEstablishmentFields = getFieldsByPath(neededFormFields?.establishments.viewMap.form.fields, ['code']);

                            store.dispatch(changeFieldProperty(neededEstablishmentFields?.code.id, 'default', newValue))
                        }
                    }
                },
                {
                    path: 'name',
                    tKey: 'denomination_spec',
                    widthClassName: "col-xs-12 col-sm-6",
                    required: true,
                    placeholder: 'Renseigner la dénomination INSEE',
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const neededFormFields = getFieldsByPath(module.viewMap.form.fields, ['establishments']);
                            const neededEstablishmentFields = getFieldsByPath(neededFormFields?.establishments.viewMap.form.fields, ['name']);
                            store.dispatch(changeFieldProperty(neededEstablishmentFields?.name.id, 'default', newValue))
                        }
                    }
                },
                {
                    path: 'rna',
                    tKey: 'rna_spec',
                    widthClassName: "col-xs-12 col-sm-6",
                    placeholder: 'Renseigner le N° du Registre National des Associations'
                },
                {
                    path: 'appellation',
                    tKey: 'appellation',
                    widthClassName: "col-xs-12 col-sm-6",
                    placeholder: "Renseigner l'appellation (sigle)"
                },
                {
                    path: 'rup',
                    widthClassName: "col-xs-12 col-sm-6",
                    default: defaultRup,
                    required: true
                },
                {
                    path: 'endowmentFund',
                    widthClassName: "col-xs-12 col-sm-6",
                    style: {height: '60px', marginTop: '6px'},
                    default: false,
                    required: true,
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const neededFormFields = getFieldsByPath(module.viewMap.form.fields, ['endowmentFundDetails']);
                            store.dispatch(setFieldVisibility(neededFormFields?.endowmentFundDetails.id, !!newValue))

                            const changedValue = typeof oldValue === 'boolean' && typeof newValue === 'boolean' && oldValue && !newValue
                            if (changedValue){
                                neededFormFields?.endowmentFundDetails.setValue('')
                            }
                        }
                    }
                },
                {
                    path: 'endowmentFundDetails',
                    type: 'textarea',
                },
                {
                    path: 'status',
                    tKey: 'status',
                    widthClassName: "col-xs-12 col-sm-6",
                    required: true,
                    placeholder: 'Sélectionner le statut',
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const neededFormFields = getFieldsByPath(module.viewMap.form.fields, ['statusDate']);
                            store.dispatch(setFieldVisibility(neededFormFields?.statusDate.id, ['suspended', 'struckOff', 'resigned', 'ceasedOperations'].includes(newValue?.id)))

                            const changedValue = !!oldValue?.id && !!newValue?.id && oldValue.id !== newValue.id
                            if (changedValue){
                                neededFormFields?.statusDate.setValue(null)
                            }
                        }
                    }
                },
                {path: 'statusDate', tKey: 'à la date du', widthClassName: "col-xs-12 col-sm-6", required: true},
                {
                    path: 'member',
                    tKey: 'organizationSize',
                    widthClassName: "col-xs-12 col-sm-6",
                    placeholder: 'Sélectionner la taille',
                    default: defaultMember,
                    required: true,
                    editable: false
                },
                {
                    path: 'activities',
                    tKey: 'activities_spec',
                    placeholder: 'Sélectionner les activités',
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const neededFormFields = getFieldsByPath(module.viewMap.form.fields, ['establishments', 'organizationType', 'otherActivity']);
                            const neededEstablishmentFields = getFieldsByPath(neededFormFields?.establishments.viewMap.form.fields, ['types']);

                            refreshOtherActivityField({store, module}, 'activities', neededFormFields?.organizationType?.getValue()?.id === 'association')
                            store.dispatch(
                                generateFetchFieldListAction(
                                    ESTABLISHMENT_TYPES_LIST,
                                    store.getState,
                                    {
                                        data: {activitiesTypes: newValue}
                                    }
                                )
                            ).then(() => relabelOtherActivityOption(store, ESTABLISHMENT_TYPES_LIST, neededFormFields?.otherActivity?.getValue()))
                        }
                    }
                },
                otherActivityFormField({
                    subscriptions: {
                        onChange: (newValue, oldValue, {store}) => relabelOtherActivityOption(store, ESTABLISHMENT_TYPES_LIST, newValue)
                    }
                }),
                {
                    path: 'animalTypes',
                    tKey: 'species',
                    placeholder: 'Sélectionner les espèces',
                    display: 'name',
                    fieldPath: ['id', 'fullName', 'code', 'name'],
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const dataLists = ["m-C-organization.Shelter_animalTypes"]

                            dataLists.forEach(dataList => store.dispatch(
                                generateFetchFieldListAction(
                                    dataList,
                                    store.getState,
                                    {
                                        data: {animalTypes: newValue}
                                    }
                                )
                            ))
                        }
                    }
                },
                'eligibleForDirectAidPayment',
                {
                    path: 'establishments',
                    tKey: 'establishments_spec',
                    type: 'accordion',
                    noRowsMessage: 'Renseigner les établissements',
                    defaultSortBy: 'code',
                    defaultSortDirection: 'ASC',
                    required: true,
                    //removable: true,
                    viewMap: {
                        dt: [
                            {path: 'code', tKey: 'siret_spec'},
                            {path: 'status', tKey: 'status'},
                            {path: 'name'},
                            {path: 'publishOnWebsite', tKey: 'publier sur le site'},
                            {path: 'zipCode', tKey: 'zipCode_spec'},
                            {path: 'city', tKey: 'city_spec', initiallyNotVisible: true},
                        ],
                        form: [
                            {path: 'code', tKey: 'siret_spec', required: true, placeholder: 'Renseigner le SIRET', exactLength: 14, numerical: true, widthClassName: "col-xs-12 col-sm-6",},
                            {
                                path: 'status',
                                tKey: 'status',
                                widthClassName: "col-xs-12 col-sm-6",
                                default: establishmentStatusObjects.find(obj => obj.id === 'headquarters'),
                                placeholder: 'Sélectionner le statut',
                                validations: [
                                    function(formValues) {
                                        return function validation(value, values) {
                                            const headquartersPreviouslyFilled = (formValues?.establishments || []).find(obj => obj.status?.id === 'headquarters')
                                            return !!headquartersPreviouslyFilled && (value?.id === 'headquarters' && values.id !== headquartersPreviouslyFilled?.id) ?
                                                'Le statut siège ne peut être affecté qu’à un seul établissement'
                                                : !headquartersPreviouslyFilled && value?.id !== 'headquarters' ?
                                                    'Veiller affecter le statut siège à un établissement'
                                                    : undefined
                                        }
                                    }
                                ]
                            },
                            {path: 'name', required: true, placeholder: 'Renseigner le nom',},
                            {path: 'publishOnWebsite', tKey: 'publier sur le site', type: 'checkbox'},
                            {
                                path: 'types',
                                tKey: 'activities_spec',
                                filters: ['byOrganizations'],
                                placeholder: 'Sélectionner les activités'
                            },
                            {path: 'animalTypes', tKey: 'species', placeholder: 'Sélectionner les espèces', display: 'name', filters: ['byOrganizations']},
                            {path: 'address', tKey: 'address_spec', placeholder: "Renseigner l'adresse", required: true},
                            {path: 'additionalAddress', tKey: 'additionalAddress_spec', placeholder: "Renseigner le complément d'adresse"},
                            {path: 'zipCode', tKey: 'zipCode_spec', widthClassName: "col-xs-12 col-sm-6", placeholder: 'Renseigner le code postal', exactLength: 5, required: true, numerical: true},
                            {path: 'city', tKey: 'city_spec', widthClassName: "col-xs-12 col-sm-6", placeholder: 'Renseigner la ville', required: true},
                            {path: 'lat', tKey: 'lat_spec', widthClassName: "col-xs-12 col-sm-6", placeholder: 'Renseigner la latitude'},
                            {path: 'lng', tKey: 'lng_spec', widthClassName: "col-xs-12 col-sm-6", placeholder: 'Renseigner la longitude'},
                            openingHoursField(),
                            {path: 'shelterSchedules', tKey: 'shelterSchedulesDetails', type: 'textarea'},
                            //{path: 'zone', tKey: 'administrativeRegion_spec', placeholder: 'Sélectionner la région administrative', display: 'fullName', fieldPath: ['id', 'fullName', 'code', 'name']},
                            {path: 'country', tKey: 'country_spec', default: 'France', hidden: true, disabled: true, required: true},
                            {
                                path: 'isAdministrativeAddressForAssociation',
                                type: 'checkbox',
                                validations: [
                                    function(formValues) {
                                        return function validation(value, values) {
                                            const administrativeAddressForAssociationPreviouslyFilled = (formValues?.establishments || [])?.find(obj => obj.isAdministrativeAddressForAssociation)
                                            return !!administrativeAddressForAssociationPreviouslyFilled && (value && values.id !== administrativeAddressForAssociationPreviouslyFilled?.id) ?
                                                'Une seule adresse de gestion peut être renseignée'
                                                : undefined
                                        }
                                    }
                                ]
                            }
                        ]
                    },
                    subscriptions: {
                        onChange: (newValue, oldValue, { module, store }) => {
                            const establishmentWithAdministrativeAddress = newValue?.find(obj => obj.isAdministrativeAddressForAssociation)
                            const neededFormFields = getFieldsByPath(module.viewMap.form.fields, ['address', 'additionalAddress', 'zipCode', 'city', 'country']);
                            const administrativeAddressAlreadyFilled = !!neededFormFields?.address?.getValue() && !!neededFormFields?.additionalAddress?.getValue() && !!neededFormFields?.zipCode?.getValue() && !!neededFormFields?.city?.getValue() && !!neededFormFields?.country?.getValue() //&& !!neededFormFields?.zone?.getValue()
                            const state = store.getState()
                            const oldSavedValuesIds = (_.get(state, 'edit.object.data.establishments') || []).map(o => o.id)

                            if(newValue) {
                                store.dispatch(
                                    setDataList(
                                        `m-C-organization.CUser_shelters`,
                                        newValue
                                            .filter(establishment => establishment.status?.id !== 'closed')
                                            .map(shelter => ({...shelter, siretNameStatus: `${shelter.code} - ${shelter.name} (${shelter.status?.name})`}))
                                            .map(o => {
                                                return oldSavedValuesIds.includes(o.id) ? {
                                                        ..._.pick(o, ['id', 'siretNameStatus'])
                                                    }
                                                    : {
                                                        _createdInTheForm: true,
                                                        ..._.pick(o, ['id', 'siretNameStatus'])
                                                    }
                                            })
                                    )
                                )
                            }

                            if (!administrativeAddressAlreadyFilled && !!establishmentWithAdministrativeAddress){
                                neededFormFields?.address?.setValue(establishmentWithAdministrativeAddress.address)
                                neededFormFields?.additionalAddress?.setValue(establishmentWithAdministrativeAddress.additionalAddress)
                                neededFormFields?.city?.setValue(establishmentWithAdministrativeAddress.city)
                                neededFormFields?.zipCode?.setValue(establishmentWithAdministrativeAddress.zipCode)
                                neededFormFields?.country?.setValue(establishmentWithAdministrativeAddress.country)
                            }
                        }
                    }
                },
                {
                    path: 'users',
                    tKey: 'contacts',
                    type: 'accordion',
                    noRowsMessage: 'Renseigner les contacts',
                    defaultSortBy: 'lastname',
                    defaultSortDirection: 'ASC',
                    //removable: true,
                    viewMap: {
                        dt: [
                            {path: 'lastname', tKey: 'lastname_spec'},
                            {path: 'firstname', tKey: 'firstname_spec'},
                            {path: 'mail', tKey: 'mail_spec'},
                            {path: 'username', initiallyNotVisible: true},
                            {path: 'phone', tKey: 'phone_spec'},
                            {path: 'functions', tKey: 'jobs_spec', display: 'fullName'},
                            {path: 'active', tKey: 'state_spec'},
                            {path: 'unsubscribed', tKey: 'Désabonné des communications', type: 'boolean', width: 100},
                        ],
                        form: [
                            {path: 'civility', tKey: 'civility_spec', required: true, sortList: false, placeholder: 'Sélectionner la civilité'},
                            {path: 'lastname', widthClassName: "col-xs-12 col-sm-6", tKey: 'lastname_spec', required: true, uppercase: true, placeholder: 'Renseigner le nom'},
                            {path: 'firstname', widthClassName: "col-xs-12 col-sm-6", tKey: 'firstname_spec', placeholder: 'Renseigner le prénom'},
                            {path: 'mail', widthClassName: "col-xs-12 col-sm-6", tKey: 'mail_spec', placeholder: 'Renseigner le courriel', emailAddress: true},
                            {path: 'username', widthClassName: "col-xs-12 col-sm-6",},
                            {path: 'phone', tKey: 'mobile', type: 'phoneNumber', widthClassName: "col-xs-12 col-sm-6",},
                            {path: 'phone2', type: 'phoneNumber', widthClassName: "col-xs-12 col-sm-6",},
                            {path: 'organizationReferent', tKey: 'organizationReferent_spec', default: false, widthClassName: "col-xs-12 col-sm-6",},
                            {path: 'liberalityReferent', tKey: 'liberalityReferent_spec', default: false, widthClassName: "col-xs-12 col-sm-6",},
                            {
                                path: 'shelters',
                                tKey: 'establishments_contact',
                                placeholder: 'Sélectionner les établissements',
                                display: 'siretNameStatus',
                                fieldPath: customFieldPath,
                                filters: ['byOrganizationAndNotClosed']
                            },
                            {path: 'functions', tKey: 'jobs_spec', placeholder: 'Sélectionner les métiers', display: 'fullName', required: true},
                            {path: 'authorizations', tKey: 'profiles_spec', placeholder: 'Sélectionner les profils', translateName: true, filters: ['thisModel']},
                            {path: 'active', default: true},
                            {path: 'language', hidden: true, default: {id: 'fr'}},
                            {path: 'status', hidden: true, default: 'waiting'}
                        ]
                    },
                    subscriptions: {
                        onChange: (newValue, oldValue, { module, store, t }) => {
                            const state = store.getState()
                            const oldSavedValuesIds = (_.get(state, 'edit.object.data.users') || []).map(o => o.id)

                            if(newValue) {
                                store.dispatch(
                                    setDataList(
                                        `m-C-organization.Refuge_contacts`,
                                        newValue
                                            .map(user => ({
                                                ...user,
                                                completeInfo: !!user.completeInfo ? user.completeInfo : `${t(user.civility.id)} ${user.firstname} ${user.lastname}, Courriel : ${user.mail} et Téléphone : ${user.phone}`
                                            }))
                                            .map(o => {
                                                return oldSavedValuesIds.includes(o.id) ? {
                                                        ..._.pick(o, ['id', 'completeInfo'])
                                                    }
                                                    : {
                                                        _createdInTheForm: true,
                                                        ..._.pick(o, ['id', 'completeInfo'])
                                                    }
                                            })
                                    )
                                )
                            }
                        }
                    }
                },
                {
                    path: 'refuges',
                    tKey: 'refuge(s) relais',
                    type: 'accordion',
                    noRowsMessage: 'Renseigner les refuges relais',
                    defaultSortBy: 'name',
                    defaultSortDirection: 'ASC',
                    //removable: true,
                    viewMap: {
                        dt: [
                            'name',
                            'address',
                            'zipCode',
                            'city',
                        ],
                        form: [
                            {path: 'name', required: true,},
                            {path: 'address', placeholder: "Renseigner l'adresse", required: true,},
                            {path: 'additionalAddress', placeholder: "Renseigner le complément d'adresse"},
                            {path: 'zipCode', widthClassName: "col-xs-12 col-sm-6", placeholder: 'Renseigner le code postal', maxLength: 5, noSpaces: true, required: true, numerical: true},
                            {path: 'city', widthClassName: "col-xs-12 col-sm-6", placeholder: 'Renseigner la ville', required: true},
                            {path: 'country', default: 'France', hidden: true, disabled: true, required: true},
                            {path: 'contacts', display: 'completeInfo', fieldPath: customUserFieldPath, filters: ['byOrganizationId']},
                        ]
                    }
                },
                {path: 'address', required: true, section: 'address'},
                {path: 'additionalAddress', section: 'address'},
                {path: 'zipCode', required: true, section: 'address', widthClassName: "col-xs-12 col-sm-6", maxLength: 5, noSpaces: true, numerical: true},
                {path: 'city', required: true, section: 'address', widthClassName: "col-xs-12 col-sm-6",},
                {
                    path: 'accountNumber',
                    tKey: 'accountNumber_spec',
                    section: 'administratif',
                    placeholder: 'Renseigner le N° de compte comptable'
                },
                {path: 'iban', placeholder: "Renseigner l'IBAN", section: 'administratif'},
                {path: 'bic', placeholder: "Renseigner le BIC correspondant à l'IBAN", section: 'administratif'},
                {
                    path: 'organizationMemberships',
                    tKey: 'contributions',
                    section: 'administratif',
                    type: 'accordion',
                    noRowsMessage: 'Association non concernée',
                    defaultSortBy: 'exercise',
                    defaultSortDirection: 'DESC',
                    disabled: true,
                    viewMap: {
                        dt: [
                            {path: 'exercise', tKey: 'exercise_spec', type: 'object', display: 'code'},
                            {path: 'status', tKey: 'status', type: 'object'},
                            {path: 'feeAmount', tKey: 'contribution'},
                            {path: 'donationAmount', tKey: 'donation'},
                            {path: 'paymentDate', tKey: 'paymentDate_spec'},
                            {path: 'paymentMethod', tKey: 'paymentMethod_spec', translate: true}
                        ]
                    }
                },
                {
                    path: 'manualOrganizationDetails',
                    tKey: 'structure',
                    section: 'administratif',
                    type: 'dtObjects',
                    noRowsMessage: 'Association non concernée',
                    defaultSortBy: 'exercise',
                    defaultSortDirection: 'DESC',
                    placeholder: 'Association non concernée',
                    required: true,
                    fields: [
                        {path: 'exercise', tKey: 'exercise_spec', type: 'object', display: 'code'},
                        {path: 'administratorsNumber', tKey: 'administratorsNumber_spec', type: 'customized'},
                        {path: 'employeesNumber', tKey: 'employeesNumber_spec', type: 'customized'},
                        {path: 'volunteersNumber', tKey: 'volunteersNumber_spec', type: 'customized'},
                        {path: 'investigatorsNumber', tKey: 'investigatorsNumber_spec', type: 'customized'},
                        {path: 'hostFamiliesNumber', tKey: 'hostFamiliesNumber_spec', type: 'customized'},
                        {path: 'ACACEDNumber', tKey: 'ACACEDNumber', type: 'customized'}
                    ]
                },

                {path: 'country', hidden: true},
                {path: "images", type: 'logoPicker', placeholder: 'addLogo', maxLength: 1, fieldPath: ['id', 'name', 'user.id', 'user.name', 'date'], section: 'logo'},
                {path: 'files', section: 'documents', tKey: "documents liés à l'association", type: 'simpleFiles', fieldPath: ['id', 'filename', 'date', 'user.id', 'user.name'],},
                {path: 'conversations', tKey: "zone d'échanges", section: 'chat'},
                {path: 'organizationDetails', hidden: true}
            ],
            onOpen: ({store}) => {
                const objectId = _.get(store.getState(), 'edit.object.data.id')
                if (objectId) {
                    store.dispatch(
                        generateFetchFieldListAction(
                            "m-C-organization.Refuge_contacts",
                            store.getState,
                            {data: {organizationId: objectId}}
                        )
                    )
                    store.dispatch(
                        generateFetchFieldListAction(
                            "m-C-organization.CUser_shelters",
                            store.getState,
                            {data: {organizationId: objectId}}
                        )
                    )
                }
            }
        }
    },
    filters: ['byType']
}

export const userModule = {
    name: 'MyOrganization',
    object: 'Organization',
    tKey: 'mTitle_characteristics',
    newable: false,
    removable: false,
    defaultSortBy: 'code',
    objectIdentifier: 'code',
    defaultSortDirection: 'ASC',
    category: {
        path: 'menuTitle_myAssociation',
        icon: 'building'
    },
    formSections: [
        {
            id: 'address',
            tKey: "Adresse de gestion",
            defaultCollapsed: true,
        },
        {
            id: 'administratif',
            tKey: "Administratif",
            defaultCollapsed: true
        },
        {
            id: 'logo',
            tKey: "Logo",
            defaultCollapsed: true,
        },
        {
            id: 'documents',
            tKey: "Documents",
            defaultCollapsed: true,
        }
    ],
    viewMap: {
        dt: [
            {path: 'tradingName'},
            {path: 'code', tKey: 'siren_spec'},
            {path: 'rna', tKey: 'rna_spec'},
            {path: 'nbEstablishments', tKey: 'establishmentsNumber'},
            {path: 'nbContacts', tKey: 'contactsNumber_spec'},
            {path: 'nbRefuges', initiallyNotVisible: true},
            {path: 'address', initiallyNotVisible: true},
            {path: 'additionalAddress', initiallyNotVisible: true},
            {path: 'zipCode'},
            {path: 'city', initiallyNotVisible: true},
            // Do not remove this field. It's used in relief fund submission
            {path: 'hasShelters', hidden: true}
        ],
        form: {
            fields: [
                {path: 'tradingName', placeholder: "Renseigner le Nom d'usage"},
                {path: 'code', tKey: 'siren_spec', disabled: true, widthClassName: "col-xs-12 col-sm-6",},
                {path: 'rna', tKey: 'rna_spec', disabled: true, widthClassName: "col-xs-12 col-sm-6",},
                {path: 'member', tKey: 'organizationSize', placeholder: 'Sélectionner la taille', required: true},
                {
                    path: 'activities',
                    tKey: 'activities_spec',
                    placeholder: 'Sélectionner les activités',
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => refreshOtherActivityField({store, module}, 'activities')
                    }
                },
                otherActivityFormField(),
                {path: 'animalTypes', tKey: 'species', placeholder: 'Sélectionner les espèces', display: 'name', fieldPath: ['id', 'fullName', 'code', 'name'],},

                {path: 'address', section: 'address', required: true,},
                {path: 'additionalAddress', section: 'address'},
                {path: 'zipCode', section: 'address', widthClassName: "col-xs-12 col-sm-6", required: true, maxLength: 5, noSpaces: true, numerical: true},
                {path: 'city', section: 'address', widthClassName: "col-xs-12 col-sm-6", required: true,},
                {path: 'country', default: 'France', hidden: true, disabled: true, required: true},

                {
                    path: 'organizationMemberships',
                    tKey: 'adhésions',
                    section: 'administratif',
                    type: 'accordion',
                    noRowsMessage: 'Association non concernée',
                    defaultSortBy: 'exercise',
                    defaultSortDirection: 'DESC',
                    disabled: true,
                    viewMap: {
                        dt: [
                            {path: 'exercise', tKey: 'exercise_spec', type: 'object', display: 'code'},
                            {path: 'status', tKey: 'status', type: 'object'},
                            {path: 'feeAmount', tKey: 'contribution'},
                            {path: 'donationAmount', tKey: 'donation'},
                            {path: 'paymentDate', tKey: 'paymentDate_spec'},
                            {path: 'paymentMethod', tKey: 'paymentMethod_spec', translate: true}
                        ]
                    }
                },
                {
                    path: 'manualOrganizationDetails',
                    tKey: 'structure',
                    section: 'administratif',
                    type: 'dtObjects',
                    noRowsMessage: 'Association non concernée',
                    defaultSortBy: 'exercise',
                    defaultSortDirection: 'DESC',
                    placeholder: 'Association non concernée',
                    required: true,
                    fields: [
                        {path: 'exercise', tKey: 'exercise_spec', type: 'object', display: 'code'},
                        {path: 'administratorsNumber', tKey: 'administratorsNumber_spec', type: 'customized'},
                        {path: 'employeesNumber', tKey: 'employeesNumber_spec', type: 'customized'},
                        {path: 'volunteersNumber', tKey: 'volunteersNumber_spec', type: 'customized'},
                        {path: 'investigatorsNumber', tKey: 'investigatorsNumber_spec', type: 'customized'},
                        {path: 'hostFamiliesNumber', tKey: 'hostFamiliesNumber_spec', type: 'customized'},
                        {path: 'ACACEDNumber', tKey: 'ACACEDNumber', type: 'customized'}
                    ]
                },
                {path: 'organizationDetails', hidden: true},

                {path: "images", type: 'logoPicker', placeholder: 'addLogo', maxLength: 1, fieldPath: ['id', 'name', 'user.id', 'user.name', 'date'], section: 'logo'},
                {path: 'colleagueFiles', section: 'documents', tKey: "documents liés à l'association", type: 'simpleFiles', fieldPath: ['id', 'filename', 'date', 'user.id', 'user.name']},
                {path: 'files', hidden: true},
            ]
        }
    },
    filters: [
        'byUser'
    ]
}
